import type { OutreachEditorRef } from '@recruit-robin/robin-theme/web-components';
import { Divisor, OutreachEditor, OutreachMessagePlugin } from '@recruit-robin/robin-theme/web-components';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { useDictionary } from '@/shared/lib/i18n';

import { useStyles } from './GenerateAiReachoutMessageEditor.styles';
import type {
  GenerateAiReachoutMessageEditorProps,
  GenerateAiReachoutMessageEditorRef,
} from './GenerateAiReachoutMessageEditor.types';

export const GenerateAiReachoutMessageEditor = forwardRef<
  GenerateAiReachoutMessageEditorRef,
  GenerateAiReachoutMessageEditorProps
>(function GenerateAiReachoutMessageEditor(
  { subject: initialSubject, message: initialMessage, tokens, showSubject, subjectEndAdornment },
  ref,
) {
  const [subjectRef, setSubjectRef] = useState<OutreachEditorRef | null>(null);
  const [messageRef, setMessageRef] = useState<OutreachEditorRef | null>(null);
  const dictionary = useDictionary();
  const classes = useStyles();

  useImperativeHandle(
    ref,
    () => ({
      getContent: () => ({
        subject: subjectRef?.getTextContent({ replaceTokens: true }) ?? initialSubject,
        message: messageRef?.serializeToHTML({ replaceTokens: true }) ?? initialMessage,
      }),
      subjectRef,
      messageRef,
    }),
    [subjectRef, messageRef, initialSubject, initialMessage],
  );

  useEffect(() => {
    if (subjectRef && initialSubject) {
      subjectRef.importFromHTML(initialSubject);
    }
  }, [subjectRef, initialSubject]);

  useEffect(() => {
    if (messageRef && initialMessage) {
      messageRef.importFromHTML(initialMessage);
    }
  }, [messageRef, initialMessage]);

  const enableMessagePlugin: Record<OutreachMessagePlugin, boolean> = useMemo(
    () => ({
      Attachment: true,
      Link: true,
      List: true,
      PreventTextFormat: false,
      Tokens: true,
      SanitizeTextPlugin: true,
    }),
    [],
  );

  return (
    <div css={classes.editorContainer}>
      <div css={classes.editorWrapper}>
        <OutreachEditor.Root
          dictionary={{
            subjectPlaceholder: '',
            messagePlaceholder: '',
            attachFile: dictionary.attachFile,
            link: {
              displayText: dictionary.displayText,
              insertLink: dictionary.insertLink,
              newLink: dictionary.newLink,
              removeLink: dictionary.removeLink,
              URL: dictionary.URL,
            },
            personalizationTokens: dictionary.templateTokens,
            signature: dictionary.signature,
            tokenEmpty: dictionary.tokensEmpty,
          }}
          values={tokens}>
          {showSubject && (
            <>
              <OutreachEditor.Subject ref={setSubjectRef} endAdornment={subjectEndAdornment} />
              <Divisor color={(c) => c.neutral.variant[30]} py={0} />
            </>
          )}
          <OutreachEditor.Message customCss={classes.message} ref={setMessageRef} enablePlugin={enableMessagePlugin} />
          <OutreachEditor.Toolbar customCss={classes.toolbar} variant="full" />
        </OutreachEditor.Root>
      </div>
    </div>
  );
});
